// ============================================
// Dispatcher Operational Pages
// Feature Pages — Professional UI
// LiveMap, ActiveJobs, JobReassignment,
// TrackingLinks, Alerts, JobHistory
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import DispatcherLayout from '../../components/dispatcher/DispatcherLayout';
import dispatcherService from '../../services/dispatcherService';
import socketService from '../../services/socket';
import api from '../../services/api';
import { decodeHtml } from '../../utils/decodeHtml';
import { groupRoutes } from '../../utils/routeUtils';
import { GoogleMap, Marker, DirectionsService, DirectionsRenderer } from '@react-google-maps/api';
import AnimatedMarker from '../../components/common/AnimatedMarker';
import DriverInfoWindow from '../../components/common/DriverInfoWindow';
import { getCarMarkerIcon } from '../../components/common/CarMarkerIcon';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { getPremiumMapOptions } from '../../utils/mapStyles';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import ConfirmationModal from '../../components/common/ConfirmationModal';



const mapContainerStyle = {
    width: '100%',
    height: '100%'
};

const defaultCenter = {
    lat: 51.5074, // Default to London
    lng: -0.1278
};

// ═══════════════════════════════════════════════
// 1. LIVE MAP
// View all active drivers on live map
// ═══════════════════════════════════════════════
export const LiveMap = () => {
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [focusType, setFocusType] = useState(null); // 'car' or 'route'
    const [routes, setRoutes] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [directionsResponses, setDirectionsResponses] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    const { isLoaded } = useGoogleMaps();
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // -- Fullscreen Map Logic --
    const mapContainerRef = React.useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showDriverPanel, setShowDriverPanel] = useState(true);

    const toggleFullscreen = useCallback(() => {
        if (!mapContainerRef.current) return;
        if (!document.fullscreenElement) {
            mapContainerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => { });
        } else {
            document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => { });
        }
    }, []);

    useEffect(() => {
        const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleChange);
        return () => document.removeEventListener('fullscreenchange', handleChange);
    }, []);

    const mapOptions = React.useMemo(() => getPremiumMapOptions(isDark), [isDark]);

    const mapRef = React.useRef(null);
    const boundsFit = React.useRef(false);

    const onLoad = useCallback(function callback(map) {
        mapRef.current = map;
    }, []);

    const onUnmount = useCallback(function callback(map) {
        mapRef.current = null;
    }, []);

    const activeDrivers = drivers.filter(d => !d.isBlocked && d.isVerified);

    // Only show drivers who have at least one assigned active route OR active/assigned private job on the map
    const allDriversOnRoute = activeDrivers.filter(d => 
        routes.some(r => r.isActive && String(r.driverId?._id || r.driverId) === String(d._id)) ||
        jobs.some(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && String(j.driverId?._id || j.driverId) === String(d._id))
    );

    const driversOnRoute = allDriversOnRoute.filter(d => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        const matchName = decodeHtml(d.name || '').toLowerCase().includes(s);
        const matchPhone = (d.phone || '').includes(s);
        
        const assignedRoute = routes.find(r => r.isActive && String(r.driverId?._id || r.driverId) === String(d._id));
        const assignedJob = jobs.find(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && String(j.driverId?._id || j.driverId) === String(d._id));
        
        const matchRoute = decodeHtml(assignedRoute?.name || '').toLowerCase().includes(s);
        const matchSchool = decodeHtml(assignedRoute?.schoolId?.name || '').toLowerCase().includes(s);
        const matchJob = decodeHtml(assignedJob?.name || '').toLowerCase().includes(s);
        const matchJobType = 'private'.includes(s);

        return matchName || matchPhone || matchRoute || matchSchool || matchJob || (assignedJob && matchJobType);
    });

    // Auto-fit bounds on first load
    useEffect(() => {
        if (!mapRef.current || driversOnRoute.length === 0 || boundsFit.current || selectedDriver) return;
        const bounds = new window.google.maps.LatLngBounds();
        let hasValid = false;
        driversOnRoute.forEach(d => {
            const coords = d.currentLocation?.coordinates || d.location?.coordinates;
            // Strict check to prevent "blue sea" default zoom issues
            if (Array.isArray(coords) && coords.length === 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number' && coords[0] !== 0 && coords[1] !== 0) {
                bounds.extend({ lat: coords[1], lng: coords[0] });
                hasValid = true;
            }
        });
        
        // Extend bounds to include routes
        routes.filter(r => r.isActive && r.schoolId).forEach(r => {
            const coords = r.schoolId.location?.coordinates;
            if (Array.isArray(coords) && coords.length === 2) {
                bounds.extend({ lat: coords[1], lng: coords[0] });
            }
            if (r.startLocation?.coordinates?.length === 2) {
                bounds.extend({ lat: r.startLocation.coordinates[1], lng: r.startLocation.coordinates[0] });
            }
            if (r.endLocation?.coordinates?.length === 2) {
                bounds.extend({ lat: r.endLocation.coordinates[1], lng: r.endLocation.coordinates[0] });
            }
        });

        // Extend bounds to include jobs
        jobs.filter(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && j.driverId).forEach(j => {
            if (j.pickupCoordinates?.length === 2) {
                bounds.extend({ lat: j.pickupCoordinates[1], lng: j.pickupCoordinates[0] });
            }
            if (j.dropoffCoordinates?.length === 2) {
                bounds.extend({ lat: j.dropoffCoordinates[1], lng: j.dropoffCoordinates[0] });
            }
        });

        if (hasValid) {
            mapRef.current.fitBounds(bounds);
            const listener = window.google.maps.event.addListener(mapRef.current, "idle", function() { 
                if (mapRef.current.getZoom() > 14) mapRef.current.setZoom(14); 
                window.google.maps.event.removeListener(listener); 
            });
            boundsFit.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driversOnRoute, selectedDriver]);

    // Pan to driver ONLY when selection changes (not on every location update — prevents jitter)
    const prevSelectedRef = React.useRef(null);
    const prevFocusTypeRef = React.useRef(null);
    useEffect(() => {
        if (!mapRef.current) return;
        
        if (prevSelectedRef.current !== selectedDriver || prevFocusTypeRef.current !== focusType) {
            prevSelectedRef.current = selectedDriver;
            prevFocusTypeRef.current = focusType;
            
            if (selectedDriver) {
                if (focusType === 'car') {
                    const d = drivers.find(d => d._id === selectedDriver);
                    const coords = d?.currentLocation?.coordinates || d?.location?.coordinates;
                    if (Array.isArray(coords) && coords.length === 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number' && coords[0] !== 0 && coords[1] !== 0) {
                        mapRef.current.panTo({ lat: coords[1], lng: coords[0] });
                        mapRef.current.setZoom(15);
                    }
                } else if (focusType === 'route') {
                    const assignedRoute = routes.find(r => String(r.driverId?._id || r.driverId) === String(selectedDriver));
                    const assignedJob = jobs.find(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && String(j.driverId?._id || j.driverId) === String(selectedDriver));
                    let boundsToFit = null;
                    if (assignedRoute && directionsResponses[assignedRoute._id]) {
                        boundsToFit = directionsResponses[assignedRoute._id].routes[0].bounds;
                    } else if (assignedJob && directionsResponses[`job-${assignedJob._id}`]) {
                        boundsToFit = directionsResponses[`job-${assignedJob._id}`].routes[0].bounds;
                    }
                    if (boundsToFit) {
                        mapRef.current.fitBounds(boundsToFit);
                    }
                }
            } else {
                // Deselected - zoom out to all drivers on route
                const bounds = new window.google.maps.LatLngBounds();
                let hasValid = false;
                driversOnRoute.forEach(d => {
                    const coords = d.currentLocation?.coordinates || d.location?.coordinates;
                    if (Array.isArray(coords) && coords.length === 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number' && coords[0] !== 0 && coords[1] !== 0) {
                        bounds.extend({ lat: coords[1], lng: coords[0] });
                        hasValid = true;
                    }
                });
                
                routes.filter(r => r.isActive && r.schoolId && r.schoolId.location).forEach(r => {
                    const coords = r.schoolId.location.coordinates;
                    if (Array.isArray(coords) && coords.length === 2) {
                        bounds.extend({ lat: coords[1], lng: coords[0] });
                    }
                    if (r.startLocation?.coordinates?.length === 2) {
                        bounds.extend({ lat: r.startLocation.coordinates[1], lng: r.startLocation.coordinates[0] });
                    }
                    if (r.endLocation?.coordinates?.length === 2) {
                        bounds.extend({ lat: r.endLocation.coordinates[1], lng: r.endLocation.coordinates[0] });
                    }
                });

                jobs.filter(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && j.driverId).forEach(j => {
                    if (j.pickupCoordinates?.length === 2) {
                        bounds.extend({ lat: j.pickupCoordinates[1], lng: j.pickupCoordinates[0] });
                    }
                    if (j.dropoffCoordinates?.length === 2) {
                        bounds.extend({ lat: j.dropoffCoordinates[1], lng: j.dropoffCoordinates[0] });
                    }
                });

                if (hasValid) {
                    mapRef.current.fitBounds(bounds);
                }
            }
        }
    }, [drivers, selectedDriver, focusType, driversOnRoute, routes, jobs, directionsResponses]);

    const fetchDriversAndRoutes = useCallback(async () => {
        try {
            const [driverRes, routeRes, jobRes] = await Promise.all([
                 dispatcherService.getDrivers(),
                 dispatcherService.getRoutes(),
                 dispatcherService.getJobs()
            ]);
            
            if (driverRes.data.success) setDrivers(driverRes.data.data.drivers || []);
            
            if (routeRes.data.success) {
                const fetchedRoutes = groupRoutes(routeRes.data.data.routes || []);
                setRoutes(prev => {
                    // Only clear directions if routes actually changed
                    const prevIds = prev.map(r => r._id).sort().join(',');
                    const newIds = fetchedRoutes.map(r => r._id).sort().join(',');
                    if (prevIds !== newIds) {
                        setDirectionsResponses({});
                    }
                    return fetchedRoutes;
                });
            }

            if (jobRes.data.success) {
                setJobs(jobRes.data.data.jobs || []);
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    const directionsCallback = useCallback(
        (response, routeId, status) => {
            if (response !== null) {
                if (status === 'OK') {
                    setDirectionsResponses(prev => {
                        if (prev[routeId]) return prev;
                        return { ...prev, [routeId]: response };
                    });
                } else {
                    console.log('Directions request failed due to ' + status);
                }
            }
        },
        []
    );

    // ── Socket connection status tracking ──
    const [socketStatus, setSocketStatus] = useState(() => socketService.isConnected() ? 'connected' : 'disconnected');
    const [showInfoCard, setShowInfoCard] = useState(false);
    const eventCountRef = React.useRef(0);
    const [eventCount, setEventCount] = useState(0);

    // ── Ensure socket is connected & joined to dispatcher room on mount ──
    useEffect(() => {
        // Belt-and-suspenders: even if App.js connected, re-verify
        const ensureConnection = () => {
            try {
                const userStr = localStorage.getItem('user');
                if (userStr && userStr !== 'undefined') {
                    const user = JSON.parse(userStr);
                    if (user && user.role && user._id) {
                        socketService.connect(user.role, user._id);
                    }
                }
            } catch (e) {
                console.warn('LiveMap: Error ensuring socket connection', e);
            }
            // Force rejoin rooms in case connection was already up but rooms were lost
            socketService.forceRejoin();
        };

        ensureConnection();

        // Track connection status changes
        const onConnect = () => {
            console.log('🗺️ LiveMap: Socket connected, joining rooms...');
            setSocketStatus('connected');
            socketService.forceRejoin();
        };
        const onDisconnect = (reason) => {
            console.warn('🗺️ LiveMap: Socket disconnected:', reason);
            setSocketStatus('disconnected');
        };
        const onReconnect = () => {
            console.log('🗺️ LiveMap: Socket reconnected, re-joining rooms...');
            setSocketStatus('connected');
            socketService.forceRejoin();
        };

        socketService.on('connect', onConnect);
        socketService.on('disconnect', onDisconnect);
        socketService.on('reconnect', onReconnect);

        return () => {
            socketService.off('connect', onConnect);
            socketService.off('disconnect', onDisconnect);
            socketService.off('reconnect', onReconnect);
        };
    }, []);

    useEffect(() => {
        fetchDriversAndRoutes();
        const interval = setInterval(fetchDriversAndRoutes, 30000);

        // Listen for real-time driver location updates
        const handleLocationUpdate = (data) => {
            eventCountRef.current += 1;
            // Update count in UI every 5 events to avoid excessive re-renders
            if (eventCountRef.current % 5 === 1) {
                setEventCount(eventCountRef.current);
            }

            setDrivers(prev => prev.map(d => {
                if (d._id === data.userId) {
                    return {
                        ...d,
                        currentLocation: {
                            type: 'Point',
                            coordinates: [Number(data.lng), Number(data.lat)]
                        },
                        heading: Number(data.heading) || 0,
                        speed: Number(data.speed) || 0,
                        isActive: true,
                        lastLocationUpdate: new Date()
                    };
                }
                return d;
            }));
        };

        const handleStatusChange = (data) => {
            setDrivers(prev => prev.map(d => 
                d._id === data.driverId ? { ...d, isActive: data.isActive } : d
            ));
        };

        socketService.on('driver:location:update', handleLocationUpdate);
        socketService.on('driver:status:changed', handleStatusChange);
        socketService.on('job:new', fetchDriversAndRoutes);
        socketService.on('job:updated', fetchDriversAndRoutes);
        socketService.on('route:updated', fetchDriversAndRoutes);
        socketService.on('driver:doc:updated', fetchDriversAndRoutes);
        socketService.on('driver:updated', fetchDriversAndRoutes);
        socketService.on('vehicle:updated', fetchDriversAndRoutes);

        return () => {
            clearInterval(interval);
            socketService.off('driver:location:update', handleLocationUpdate);
            socketService.off('driver:status:changed', handleStatusChange);
            socketService.off('job:new', fetchDriversAndRoutes);
            socketService.off('job:updated', fetchDriversAndRoutes);
            socketService.off('route:updated', fetchDriversAndRoutes);
            socketService.off('driver:doc:updated', fetchDriversAndRoutes);
            socketService.off('driver:updated', fetchDriversAndRoutes);
            socketService.off('vehicle:updated', fetchDriversAndRoutes);
        };
    }, [fetchDriversAndRoutes]);

    const avatarGradients = ['from-emerald-400 to-teal-500', 'from-sky-400 to-blue-500', 'from-amber-400 to-orange-500', 'from-rose-400 to-pink-500', 'from-violet-400 to-purple-500'];

    return (
        <DispatcherLayout title="Live Map" subtitle="Real-time driver tracking" searchTerm={searchTerm} onSearchChange={setSearchTerm}>
            <div 
                ref={mapContainerRef}
                className={isFullscreen ? 'fixed inset-0 z-[100] bg-slate-50 dark:bg-slate-900 flex flex-col lg:flex-row p-4 sm:p-6 gap-4 sm:gap-6 overflow-hidden' : 'flex flex-col lg:flex-row gap-6'}
            >
                {/* Map Area */}
                <div 
                    className={`relative group flex-1 bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl ${isFullscreen ? 'h-full' : 'min-h-[500px]'}`}
                >
                    {/* Toggle Panel Button (Visible when list is hidden) */}
                    {!showDriverPanel && (
                        <div className="absolute top-6 right-20 z-20 flex gap-2">
                            <button
                                onClick={() => setShowDriverPanel(true)}
                                className="w-10 h-10 rounded-xl bg-indigo-500/90 backdrop-blur-md text-white flex items-center justify-center hover:bg-indigo-600 transition-all shadow-lg active:scale-95 border border-white/20"
                                title="Show Drivers List"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="8" y1="6" x2="21" y2="6" />
                                    <line x1="8" y1="12" x2="21" y2="12" />
                                    <line x1="8" y1="18" x2="21" y2="18" />
                                    <line x1="3" y1="6" x2="3.01" y2="6" />
                                    <line x1="3" y1="12" x2="3.01" y2="12" />
                                    <line x1="3" y1="18" x2="3.01" y2="18" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {/* Fullscreen Toggle Button */}
                    <div className="absolute top-6 right-6 z-20 flex gap-2">
                        <button
                            onClick={toggleFullscreen}
                            className="w-10 h-10 rounded-xl bg-slate-900/80 dark:bg-slate-800/80 backdrop-blur-md text-white flex items-center justify-center hover:bg-slate-800 dark:hover:bg-slate-700 transition-all shadow-lg active:scale-95 border border-white/10"
                            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? (
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="4 14 10 14 10 20" />
                                    <polyline points="20 10 14 10 14 4" />
                                    <line x1="14" y1="10" x2="21" y2="3" />
                                    <line x1="3" y1="21" x2="10" y2="14" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 3 21 3 21 9" />
                                    <polyline points="9 21 3 21 3 15" />
                                    <line x1="21" y1="3" x2="14" y2="10" />
                                    <line x1="3" y1="21" x2="10" y2="14" />
                                </svg>
                            )}
                        </button>
                    </div>
                    {/* Socket Connection Status Badge */}
                    <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-[10px] font-semibold shadow-lg">
                        <span className={`w-2 h-2 rounded-full ${socketStatus === 'connected' ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                        <span className={socketStatus === 'connected' ? 'text-emerald-300' : 'text-red-300'}>
                            {socketStatus === 'connected' ? 'LIVE' : 'OFFLINE'}
                        </span>
                        {eventCount > 0 && (
                            <span className="text-slate-400 ml-0.5">• {eventCount} events</span>
                        )}
                    </div>
                    {/* Google Map */}
                    <div className="absolute inset-0">
                    {isLoaded ? (
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={defaultCenter}
                            zoom={11}
                            onLoad={onLoad}
                            onUnmount={onUnmount}
                            options={mapOptions}
                        >
                            {/* Real-time Driver Markers will go here */}
                             {driversOnRoute.filter(d => (!searchTerm || (d.name && d.name.toLowerCase().includes(searchTerm.toLowerCase())) || (d.email && d.email.toLowerCase().includes(searchTerm.toLowerCase())) || (d.phone && d.phone.includes(searchTerm)))).map((driver) => {
                                // Hide all cars UNLESS this specific driver is selected
                                if (selectedDriver && selectedDriver !== driver._id) return null;

                                const coords = driver.currentLocation?.coordinates || driver.location?.coordinates;
                                const position = (Array.isArray(coords) && coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1]) && coords[0] !== 0 && coords[1] !== 0)
                                    ? { lat: Number(coords[1]), lng: Number(coords[0]) }
                                    : { lat: 51.5074, lng: -0.1278 };
                                const driverSpeed = driver.speed || 0;
                                const markerState = selectedDriver === driver._id ? 'selected' : driver.isActive ? 'active' : 'idle';
                                const originalIndex = allDriversOnRoute.findIndex(d => d._id === driver._id);
                                    
                                return (
                                    <AnimatedMarker
                                        key={driver._id}
                                        map={mapRef.current}
                                        position={position}
                                        heading={driver.heading || 0}
                                        speed={driverSpeed}
                                        iconGenerator={(heading) => getCarMarkerIcon(markerState, heading, 0.6, originalIndex !== -1 ? originalIndex : 0)}
                                        onClick={() => {
                                            setSelectedDriver(driver._id);
                                            setShowInfoCard(true);
                                        }}
                                    />
                                );
                            })}

                            {/* Driver Info Popup */}
                            {selectedDriver && (() => {
                                const d = drivers.find(dr => dr._id === selectedDriver);
                                if (!d) return null;
                                const coords = d.currentLocation?.coordinates || d.location?.coordinates;
                                const position = (Array.isArray(coords) && coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1]) && coords[0] !== 0 && coords[1] !== 0)
                                    ? { lat: Number(coords[1]), lng: Number(coords[0]) }
                                    : { lat: 51.5074, lng: -0.1278 };
                                const assignedRoute = routes.find(r => {
                                    const rid = r.driverId?._id || r.driverId;
                                    return String(rid) === String(selectedDriver) && r.isActive;
                                });
                                const assignedJob = jobs.find(j => {
                                    const jid = j.driverId?._id || j.driverId;
                                    return String(jid) === String(selectedDriver) && (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress');
                                });
                                return (
                                    <DriverInfoWindow
                                        driverObj={d}
                                        position={position}
                                        assignedRoute={assignedRoute}
                                        assignedJob={assignedJob}
                                        onClose={() => setShowInfoCard(false)}
                                        showInfoCard={showInfoCard}
                                        setShowInfoCard={setShowInfoCard}
                                    />
                                );
                            })()}
                            
                            {/* Render Active School Routes via Directions API */}
                            {routes.filter(r => r.isActive && r.driverId && r.schoolId && (!searchTerm || (r.name && r.name.toLowerCase().includes(searchTerm.toLowerCase())) || (r.driverId && r.driverId.name && r.driverId.name.toLowerCase().includes(searchTerm.toLowerCase())))).map(route => {
                                const routeDriverId = String(route.driverId?._id || route.driverId);
                                
                                let schoolPos = null;
                                if (route.schoolId.location?.coordinates?.length === 2) {
                                    schoolPos = { lat: route.schoolId.location.coordinates[1], lng: route.schoolId.location.coordinates[0] };
                                }
                                
                                // Professionally calculate start position
                                let startPos;
                                if (route.startLocation?.coordinates?.length === 2) {
                                    startPos = { lat: route.startLocation.coordinates[1], lng: route.startLocation.coordinates[0] };
                                } else if (route.children && route.children.length > 0 && route.children[0].pickupLocation?.coordinates?.length === 2) {
                                    startPos = { lat: route.children[0].pickupLocation.coordinates[1], lng: route.children[0].pickupLocation.coordinates[0] };
                                } else {
                                    const driverObj = driversOnRoute.find(d => String(d._id) === routeDriverId);
                                    if (driverObj && driverObj.currentLocation?.coordinates?.length === 2) {
                                        startPos = { lat: driverObj.currentLocation.coordinates[1], lng: driverObj.currentLocation.coordinates[0] };
                                    } else if (schoolPos) {
                                        startPos = { lat: schoolPos.lat - 0.02, lng: schoolPos.lng - 0.02 };
                                    } else {
                                        startPos = route.schoolId.address || route.schoolId.name || 'London, UK';
                                    }
                                }

                                let endPos;
                                if (route.endLocation?.coordinates?.length === 2) {
                                    endPos = { lat: route.endLocation.coordinates[1], lng: route.endLocation.coordinates[0] };
                                } else if (schoolPos) {
                                    endPos = schoolPos;
                                } else {
                                    endPos = route.schoolId.address || route.schoolId.name || 'London, UK';
                                }

                                // Add waypoints for children to generate A, B, C markers on the map
                                const waypoints = (route.children || [])
                                    .filter(c => c.pickupLocation?.coordinates?.length === 2)
                                    .map(c => ({
                                        location: { lat: c.pickupLocation.coordinates[1], lng: c.pickupLocation.coordinates[0] },
                                        stopover: true
                                    }));
                                
                                if (directionsResponses[route._id]) {
                                    const driverIndex = driversOnRoute.findIndex(d => String(d._id) === routeDriverId);
                                    const routeColor = driverIndex >= 0 ? ['#10b981', '#0ea5e9', '#f59e0b', '#f43f5e', '#8b5cf6'][driverIndex % 5] : '#8b5cf6';
                                    const isThisDriverSelected = selectedDriver && selectedDriver === routeDriverId;
                                    // const isAnyDriverSelected = !!selectedDriver;
                                    
                                    if (selectedDriver && !isThisDriverSelected) return null;

                                    // Extract marker positions
                                    const markersToDraw = [];
                                    directionsResponses[route._id].routes[0].legs.forEach((l, idx) => {
                                        markersToDraw.push({ pos: { lat: l.start_location.lat(), lng: l.start_location.lng() }, type: idx === 0 ? 'start' : 'waypoint' });
                                    });
                                    const lastLeg = directionsResponses[route._id].routes[0].legs[directionsResponses[route._id].routes[0].legs.length - 1];
                                    markersToDraw.push({ pos: { lat: lastLeg.end_location.lat(), lng: lastLeg.end_location.lng() }, type: 'end' });

                                    return (
                                        <React.Fragment key={`frag-${route._id}`}>
                                            <DirectionsRenderer
                                                options={{
                                                    directions: directionsResponses[route._id],
                                                    preserveViewport: true,
                                                    suppressMarkers: true,
                                                    polylineOptions: {
                                                        strokeColor: routeColor,
                                                        strokeWeight: isThisDriverSelected ? 6 : 4,
                                                        strokeOpacity: 1.0,
                                                        zIndex: isThisDriverSelected ? 50 : 1
                                                    }
                                                }}
                                            />
                                            {markersToDraw.map((m, i) => (
                                                <Marker key={`m-${i}`} position={m.pos} icon={{ path: window.google.maps.SymbolPath.CIRCLE, fillColor: m.type === 'start' ? '#10b981' : m.type === 'end' ? '#f43f5e' : '#f59e0b', fillOpacity: 1, strokeWeight: 2, strokeColor: 'white', scale: m.type === 'waypoint' ? 6 : 8 }} zIndex={isThisDriverSelected ? 51 : 2} />
                                            ))}
                                        </React.Fragment>
                                    );
                                } else {
                                    return (
                                        <DirectionsService
                                            key={`svc-${route._id}`}
                                            options={{
                                                origin: route.type === 'morning' ? startPos : endPos,
                                                destination: route.type === 'morning' ? endPos : startPos,
                                                waypoints: route.type === 'morning' ? waypoints : [...waypoints].reverse(),
                                                travelMode: 'DRIVING'
                                            }}
                                            callback={(response, status) => directionsCallback(response, route._id, status)}
                                        />
                                    );
                                }
                            })}

                            {/* Render Active Private Job Routes via Directions API */}
                            {jobs.filter(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && j.driverId).map(job => {
                                const jobDriverId = String(job.driverId?._id || job.driverId);
                                const pickupPos = job.pickupCoordinates?.length === 2
                                    ? { lat: job.pickupCoordinates[1], lng: job.pickupCoordinates[0] }
                                    : null;
                                const dropoffPos = job.dropoffCoordinates?.length === 2
                                    ? { lat: job.dropoffCoordinates[1], lng: job.dropoffCoordinates[0] }
                                    : null;
                                const origin = pickupPos || job.pickupAddress;
                                const dest = dropoffPos || job.dropoffAddress;
                                if (!origin || !dest) return null;

                                if (directionsResponses[`job-${job._id}`]) {
                                    const driverIndex = driversOnRoute.findIndex(d => String(d._id) === jobDriverId);
                                    const routeColor = driverIndex >= 0 ? ['#10b981', '#0ea5e9', '#f59e0b', '#f43f5e', '#8b5cf6'][driverIndex % 5] : '#8b5cf6';
                                    
                                    const isThisJobSelected = selectedDriver && selectedDriver === jobDriverId;
                                    if (selectedDriver && !isThisJobSelected) return null;

                                    const leg = directionsResponses[`job-${job._id}`].routes[0].legs[0];
                                    const markersToDraw = [
                                        { pos: { lat: leg.start_location.lat(), lng: leg.start_location.lng() }, type: 'start' },
                                        { pos: { lat: leg.end_location.lat(), lng: leg.end_location.lng() }, type: 'end' }
                                    ];

                                    return (
                                        <React.Fragment key={`frag-job-${job._id}`}>
                                            <DirectionsRenderer
                                                options={{
                                                    directions: directionsResponses[`job-${job._id}`],
                                                    preserveViewport: true,
                                                    suppressMarkers: true,
                                                    polylineOptions: {
                                                        strokeColor: routeColor,
                                                        strokeWeight: isThisJobSelected ? 6 : 4,
                                                        strokeOpacity: 1.0,
                                                        zIndex: isThisJobSelected ? 50 : 1
                                                    }
                                                }}
                                            />
                                            {markersToDraw.map((m, i) => (
                                                <Marker key={`m-job-${i}`} position={m.pos} icon={{ path: window.google.maps.SymbolPath.CIRCLE, fillColor: m.type === 'start' ? '#10b981' : '#f43f5e', fillOpacity: 1, strokeWeight: 2, strokeColor: 'white', scale: 8 }} zIndex={isThisJobSelected ? 51 : 2} />
                                            ))}
                                        </React.Fragment>
                                    );
                                } else {
                                    return (
                                        <DirectionsService
                                            key={`svc-job-${job._id}`}
                                            options={{
                                                origin,
                                                destination: dest,
                                                travelMode: 'DRIVING'
                                            }}
                                            callback={(response, status) => directionsCallback(response, `job-${job._id}`, status)}
                                        />
                                    );
                                }
                            })}
                        </GoogleMap>
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-slate-50">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                        </div>
                    )}
                    </div>





                </div>

                {/* Driver List Panel */}
                {showDriverPanel && (
                    <div className={`w-full lg:w-80 bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl flex flex-col ${isFullscreen ? 'h-full' : ''}`} style={{ minHeight: isFullscreen ? 'auto' : '500px' }}>
                        <div className="p-6 border-b border-slate-200/50 dark:border-white/5 flex justify-between items-start">
                            <div>
                                <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">Drivers on Route</h3>
                                <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">Only drivers with assigned routes</p>
                            </div>
                            <button 
                                onClick={() => setShowDriverPanel(false)} 
                                className="p-2 -mr-2 -mt-2 text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                                title="Hide List"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3 space-y-1">
                        {loading ? (
                            [...Array(6)].map((_, i) => (
                                <div key={i} className="animate-pulse flex items-center gap-3 p-3">
                                    <div className="w-9 h-9 bg-slate-100 rounded-xl" />
                                    <div className="flex-1 space-y-1.5"><div className="h-3 bg-slate-100 rounded w-24" /><div className="h-2 bg-slate-50 rounded w-16" /></div>
                                </div>
                            ))
                        ) : drivers.length === 0 ? (
                            <div className="py-12 text-center">
                                <div className="w-12 h-12 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                                    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-slate-400">
                                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
                                    </svg>
                                </div>
                                <p className="text-sm text-slate-400 font-medium">No drivers yet</p>
                                <p className="text-[11px] text-slate-300 mt-1">Add drivers via Admin Panel</p>
                            </div>
                        ) : (
                            <>
                                {/* ── Drivers with Assigned Routes ── */}
                                {driversOnRoute.map((d, i) => (
                                    <div key={d._id}
                                        className={`w-full flex items-center gap-4 p-3.5 rounded-2xl transition-all duration-300 text-left hover:scale-[1.02] ${selectedDriver === d._id ? 'bg-emerald-500/10 border border-emerald-500/30 shadow-sm' : 'hover:bg-slate-100/50 dark:hover:bg-slate-800/50'
                                            }`}>
                                        <div className="relative shrink-0">
                                            <div className={`w-11 h-11 rounded-[14px] bg-gradient-to-br ${avatarGradients[i % 5]} flex items-center justify-center text-[11px] font-black text-white shadow-lg`}>
                                                {d.name?.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-slate-800 shadow-sm bg-emerald-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{d.name}</p>
                                            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-0.5">
                                                {(() => {
                                                    const assignedRoute = routes.find(r => (r.driverId?._id || r.driverId) === d._id);
                                                    const assignedJob = jobs.find(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && String(j.driverId?._id || j.driverId) === String(d._id));
                                                    return assignedRoute ? decodeHtml(assignedRoute.name) : (assignedJob ? decodeHtml(assignedJob.name) : 'On Duty');
                                                })()}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (selectedDriver === d._id && focusType === 'route') {
                                                        setSelectedDriver(null);
                                                        setFocusType(null);
                                                    } else {
                                                        setSelectedDriver(d._id);
                                                        setFocusType('route');
                                                    }
                                                }}
                                                title="Focus Map on Route Path"
                                                className={`p-2 rounded-xl transition-all duration-300 ${selectedDriver === d._id && focusType === 'route' ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-500/10'}`}
                                            >
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (selectedDriver === d._id && focusType === 'car') {
                                                        setSelectedDriver(null);
                                                        setFocusType(null);
                                                    } else {
                                                        setSelectedDriver(d._id);
                                                        setFocusType('car');
                                                    }
                                                }}
                                                title="Focus Map on Live Car Location"
                                                className={`p-2 rounded-xl transition-all duration-300 ${selectedDriver === d._id && focusType === 'car' ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10'}`}
                                            >
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedDriver(d._id);
                                                    setShowInfoCard(true);
                                                }}
                                                title="Show Driver Info"
                                                className={`p-2 rounded-xl transition-all duration-300 ${selectedDriver === d._id && showInfoCard ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10'}`}
                                            >
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {/* ── Unverified Drivers Section ── */}
                                {drivers.filter(d => !d.isBlocked && !d.isVerified).length > 0 && (
                                    <>
                                        <div className="pt-4 pb-2 px-3">
                                            <div className="flex items-center gap-2">
                                                <div className="h-px flex-1 bg-amber-300/30 dark:bg-amber-500/20" />
                                                <span className="text-[9px] font-black tracking-widest uppercase text-amber-500 dark:text-amber-400">Unverified</span>
                                                <div className="h-px flex-1 bg-amber-300/30 dark:bg-amber-500/20" />
                                            </div>
                                        </div>
                                        {drivers.filter(d => !d.isBlocked && !d.isVerified).map((d, i) => (
                                            <div key={d._id}
                                                className="w-full flex items-center gap-4 p-3.5 rounded-2xl opacity-70 bg-amber-50/30 dark:bg-amber-500/5 border border-amber-200/30 dark:border-amber-500/10">
                                                <div className="relative">
                                                    <div className="w-11 h-11 rounded-[14px] bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[11px] font-black text-white shadow-lg">
                                                        {d.name?.split(' ').map(n => n[0]).join('')}
                                                    </div>
                                                    <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-slate-800 shadow-sm bg-amber-500" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300 truncate">{d.name}</p>
                                                    <p className="text-[10px] font-black tracking-widest uppercase text-amber-600 dark:text-amber-400 mt-0.5">
                                                        <span className="flex items-center gap-1"><svg className="w-3.5 h-3.5 flex-shrink-0 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Docs Pending</span>
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}

                                {/* ── Inactive / Blocked Drivers Section ── */}
                                {drivers.filter(d => d.isBlocked).length > 0 && (
                                    <>
                                        <div className="pt-4 pb-2 px-3">
                                            <div className="flex items-center gap-2">
                                                <div className="h-px flex-1 bg-slate-200/50 dark:bg-slate-700/50" />
                                                <span className="text-[9px] font-black tracking-widest uppercase text-slate-400 dark:text-slate-500">Inactive</span>
                                                <div className="h-px flex-1 bg-slate-200/50 dark:bg-slate-700/50" />
                                            </div>
                                        </div>
                                        {drivers.filter(d => d.isBlocked).map((d, i) => (
                                            <div key={d._id}
                                                className="w-full flex items-center gap-4 p-3.5 rounded-2xl opacity-50 cursor-default">
                                                <div className="relative">
                                                    <div className="w-11 h-11 rounded-[14px] bg-slate-300 dark:bg-slate-700 flex items-center justify-center text-[11px] font-black text-white/70">
                                                        {d.name?.split(' ').map(n => n[0]).join('')}
                                                    </div>
                                                    <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-slate-800 shadow-sm ${d.isBlocked ? 'bg-rose-500' : 'bg-slate-400'}`} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-slate-500 dark:text-slate-400 truncate">{d.name}</p>
                                                    <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400 dark:text-slate-500 mt-0.5">
                                                        {d.isBlocked ? 'Inactive' : 'Inactive'}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    </div>
                )}
            </div>
        </DispatcherLayout>
    );
};


// 2. ACTIVE JOBS
// View job status: Assigned, In progress, Completed
// ═══════════════════════════════════════════════
export const ActiveJobs = () => {
    const toast = useToast();
    const [jobsData, setJobsData] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [actionLoading, setActionLoading] = useState(null);
    const [reopenModal, setReopenModal] = useState({ isOpen: false, id: null, type: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [conflictModal, setConflictModal] = useState({ isOpen: false, message: '' });

    const handleReopenMission = async () => {
        const { id, type } = reopenModal;
        if (!id) return;
        
        setActionLoading(true); // Simplified to true since modal handles context
        try {
            const endpoint = type === 'route' ? `/dispatcher/routes/${id}/reopen` : `/dispatcher/jobs/${id}/reopen`;
            await api.patch(endpoint);
            toast.success(`${type === 'route' ? 'Route' : 'Job'} reopened successfully`);
            await fetchAllJobs();
        } catch (err) {
            console.error(`Error reopening ${type}:`, err);
            toast.error(`Failed to reopen ${type}`);
        } finally {
            setActionLoading(false);
            setReopenModal({ isOpen: false, id: null, type: null });
        }
    };

    const initialLoadDone = React.useRef(false);

    const fetchAllJobs = useCallback(async () => {
        try {
            if (!initialLoadDone.current) setLoading(true);
            const [routesRes, jobsRes, driversRes] = await Promise.all([
                dispatcherService.getRoutes(),
                dispatcherService.getJobs(),
                dispatcherService.getDrivers()
            ]);
            
            if (driversRes.data.success) setDrivers(driversRes.data.data.drivers || []);
            
            let combined = [];

            if (routesRes.data.success) {
                const routes = groupRoutes(routesRes.data.data.routes || []);
                const formattedRoutes = routes.map((r, i) => ({
                    _id: r._id,
                    rawType: 'ROUTE',
                    id: `RTE-${String(i + 1).padStart(3, '0')}`,
                    route: decodeHtml(r.name),
                    driver: r.driverId?.name || 'Unassigned',
                    driverId: r.driverId?._id || null,
                    school: decodeHtml(r.schoolId?.name) || 'Private',
                    type: r.type || 'morning',
                    status: r.status === 'started' ? 'In Progress' 
                        : r.status === 'completed' 
                            ? (r.completionStatus === 'issue' ? 'Completed with Issues' : 'Completed') 
                            : r.driverId ? 'Assigned' : 'Pending',
                    completionStatus: r.completionStatus || null,
                    completionNote: r.completionNote || '',
                    time: r.scheduledTime || '—',
                }));
                combined = [...combined, ...formattedRoutes];
            }

            if (jobsRes.data.success) {
                const pJobs = jobsRes.data.data.jobs || [];
                const formattedJobs = pJobs.map((j, i) => ({
                    _id: j._id,
                    rawType: 'JOB',
                    id: `JOB-${String(i + 1).padStart(3, '0')}`,
                    route: j.name,
                    driver: j.driverId?.name || 'Unassigned',
                    driverId: j.driverId?._id || null,
                    school: 'Private Job',
                    type: 'PRIVATE',
                    status: j.status === 'started' ? 'In Progress' 
                        : j.status === 'completed' 
                            ? (j.completionStatus === 'issue' ? 'Completed with Issues' : 'Completed') 
                            : (j.status === 'accepted' || j.driverId) ? 'Assigned' : 'Pending',
                    completionStatus: j.completionStatus || null,
                    completionNote: j.completionNote || '',
                    time: j.scheduledTime || '—',
                }));
                combined = [...combined, ...formattedJobs];
            }

            setJobsData(combined);
        } catch (err) { console.error(err); }
        finally { setLoading(false); initialLoadDone.current = true; }
    }, []);

    useEffect(() => { 
        fetchAllJobs(); 
        
        // Polling fallback: refresh every 10 seconds regardless of socket
        const interval = setInterval(fetchAllJobs, 10000);

        // Socket listeners for instant updates
        socketService.on('job:new', fetchAllJobs);
        socketService.on('job:updated', fetchAllJobs);
        socketService.on('route:updated', fetchAllJobs);
        socketService.on('driver:updated', fetchAllJobs);
        socketService.on('vehicle:updated', fetchAllJobs);

        return () => {
            clearInterval(interval);
            socketService.off('job:new', fetchAllJobs);
            socketService.off('job:updated', fetchAllJobs);
            socketService.off('route:updated', fetchAllJobs);
            socketService.off('driver:updated', fetchAllJobs);
            socketService.off('vehicle:updated', fetchAllJobs);
        };
    }, [fetchAllJobs]);

    const filteredJobs = jobsData.filter(j => {
        const matchesStatus = statusFilter === 'all' || j.status === statusFilter;
        if (!matchesStatus) return false;
        if (!searchTerm) return true;
        
        const s = searchTerm.toLowerCase();
        return (
            (j.id && j.id.toLowerCase().includes(s)) ||
            (j.route && j.route.toLowerCase().includes(s)) ||
            (j.driver && j.driver.toLowerCase().includes(s)) ||
            (j.school && j.school.toLowerCase().includes(s))
        );
    });

    const handleAssign = async (job, driverId) => {
        try {
            if (!driverId) {
                // Unassign logic
                if (job.rawType === 'ROUTE') {
                    if (String(job._id).startsWith('merged_')) {
                        const parts = String(job._id).split('_');
                        if (parts.length >= 3) {
                            await dispatcherService.reassignRoute(parts[1], { driverId: null, isMerged: true });
                            await dispatcherService.reassignRoute(parts[2], { driverId: null, skipDriverNotification: true, skipPaNotification: true });
                        }
                    } else {
                        await dispatcherService.reassignRoute(job._id, { driverId: null });
                    }
                } else {
                    await dispatcherService.unassignJob(job._id);
                }
            } else {
                // Assign logic
                if (job.rawType === 'ROUTE') {
                    if (String(job._id).startsWith('merged_')) {
                        const parts = String(job._id).split('_');
                        if (parts.length >= 3) {
                            await dispatcherService.reassignRoute(parts[1], { driverId, isMerged: true });
                            await dispatcherService.reassignRoute(parts[2], { driverId, skipDriverNotification: true, skipPaNotification: true });
                        }
                    } else {
                        await dispatcherService.reassignRoute(job._id, { driverId });
                    }
                } else {
                    await dispatcherService.assignJobToDriver(job._id, driverId);
                }
            }
            toast.success('Assignment updated successfully');
            fetchAllJobs(); // Refresh
        } catch (err) {
            console.error('Reassignment failed:', err);
            const msg = err.response?.data?.message || 'Reassignment failed';
            if (err.response?.status === 409) {
                const isPaConflict = msg.toLowerCase().startsWith('pa ');
                setConflictModal({ isOpen: true, message: msg, isPa: isPaConflict });
            } else {
                toast.error(msg);
            }
        }
    };

    const statusStyles = {
        'Completed': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
        'Completed with Issues': { bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
        'In Progress': { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
        'Assigned': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
        'Pending': { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
    };

    const avatarGradients = ['from-emerald-400 to-teal-500', 'from-sky-400 to-blue-500', 'from-amber-400 to-orange-500', 'from-rose-400 to-pink-500'];

    return (
        <DispatcherLayout title="Active Jobs" subtitle="Job status tracking & monitoring" searchTerm={searchTerm} onSearchChange={setSearchTerm}>
            {/* Status Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 relative z-10">
                {[
                    { label: 'Total Jobs', value: jobsData.length, gradient: 'from-slate-500 to-slate-700', shadow: 'shadow-slate-500/20' },
                    { label: 'Assigned', value: jobsData.filter(j => j.status === 'Assigned').length, gradient: 'from-amber-500 to-orange-600', shadow: 'shadow-amber-500/20' },
                    { label: 'In Progress', value: jobsData.filter(j => j.status === 'In Progress').length, gradient: 'from-blue-500 to-sky-600', shadow: 'shadow-blue-500/20' },
                    { label: 'Completed', value: jobsData.filter(j => j.status === 'Completed' || j.status === 'Completed with Issues').length, gradient: 'from-emerald-500 to-teal-600', shadow: 'shadow-emerald-500/20' },
                ].map((s, i) => (
                    <div key={i} className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] p-6 border border-white/60 dark:border-white/10 shadow-sm dark:shadow-2xl relative overflow-hidden group hover:scale-[1.03] hover:shadow-xl dark:hover:shadow-black/60 transition-all duration-300">
                        <div className={`absolute -top-8 -right-8 w-24 h-24 bg-gradient-to-br ${s.gradient} opacity-[0.08] dark:opacity-[0.15] rounded-full blur-xl group-hover:opacity-[0.2] transition-opacity`} />
                        <p className="text-3xl font-black text-slate-900 dark:text-white relative tracking-tight">{loading ? '...' : s.value}</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest mt-1.5 relative">{s.label}</p>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 relative z-10 w-full max-w-full min-w-0">
                <div className="flex rounded-xl overflow-x-auto scrollbar-hide border border-white/60 dark:border-white/10 shadow-sm w-full sm:w-auto max-w-full min-w-0 snap-x">
                    {['all', 'Assigned', 'In Progress', 'Completed', 'Pending'].map(s => (
                        <button key={s} onClick={() => setStatusFilter(s)}
                            className={`shrink-0 snap-start whitespace-nowrap px-5 py-2.5 text-[11px] uppercase tracking-widest font-black transition-all duration-300 ${statusFilter === s ? 'bg-emerald-500 text-white shadow-inner' : 'bg-white/60 dark:bg-slate-900/40 backdrop-blur-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}>{s === 'all' ? 'All' : s}</button>
                    ))}
                </div>
                <button onClick={fetchAllJobs} className="ml-auto px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 bg-emerald-50/80 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 border border-emerald-200/50 dark:border-emerald-500/20 transition-all duration-300 hover:scale-105 shadow-sm">
                    ↻ Refresh
                </button>
            </div>

            {/* Jobs Table */}
            <div className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl relative z-10">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-50/50 dark:bg-slate-800/30">
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Job ID</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Route</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Driver</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">School</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Type</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Status</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Time</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {loading ? (
                                [...Array(4)].map((_, i) => (
                                    <tr key={i} className="animate-pulse"><td colSpan="7" className="px-6 py-5"><div className="h-5 bg-slate-100 dark:bg-slate-800 rounded-lg" /></td></tr>
                                ))
                            ) : filteredJobs.length === 0 ? (
                                <tr><td colSpan="7" className="px-6 py-16 text-center text-sm text-slate-400 font-medium italic">No jobs match the filter</td></tr>
                            ) : (
                                filteredJobs.map((job, i) => (
                                    <React.Fragment key={i}>
                                    <tr className="hover:bg-teal-500/5 dark:hover:bg-teal-400/5 transition-colors duration-200">
                                        <td className="px-6 py-5 text-sm font-mono font-bold text-emerald-600 dark:text-emerald-400">{job.id}</td>
                                        <td className="px-6 py-5 text-sm font-bold text-slate-900 dark:text-white">{job.route}</td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${avatarGradients[i % 4]} flex items-center justify-center text-white text-[10px] font-black shadow-lg shadow-${avatarGradients[i % 4].split('-')[1]}-500/20`}>
                                                    {job.driver.split(' ').map(n => n[0]).join('')}
                                                </div>
                                                <select 
                                                    value={job.driverId || ''} 
                                                    onChange={(e) => handleAssign(job, e.target.value)}
                                                    className={`text-[11px] px-2 py-1.5 rounded-lg border outline-none font-bold tracking-wide transition-colors ${
                                                        !job.driverId ? 'border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400' : 'border-slate-200/50 dark:border-slate-700 bg-transparent text-slate-600 dark:text-slate-300 focus:border-emerald-500 cursor-pointer'
                                                    }`}
                                                >
                                                    <option value="" className="text-slate-900 bg-white dark:bg-slate-800 dark:text-white">— Unassigned —</option>
                                                    {drivers.filter(d => !d.isBlocked && d.isVerified).map(d => (
                                                        <option key={d._id} value={d._id} className="text-slate-900 bg-white dark:bg-slate-800 dark:text-white">{d.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-sm font-semibold text-slate-600 dark:text-slate-300">{decodeHtml(job.school)}</td>
                                        <td className="px-6 py-5">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest ${
                                                job.type === 'PRIVATE' ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20' :
                                                job.type === 'morning' ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-500/20' : 
                                                'bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-100 dark:border-sky-500/20'
                                                }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${
                                                    job.type === 'PRIVATE' ? 'bg-indigo-500' :
                                                    job.type === 'morning' ? 'bg-amber-500' : 'bg-sky-500'
                                                }`} />
                                                {job.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider ${statusStyles[job.status]?.bg || 'bg-slate-100 dark:bg-slate-800'} ${statusStyles[job.status]?.text || 'text-slate-600 dark:text-slate-400'}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${statusStyles[job.status]?.dot || 'bg-slate-400'}`} />
                                                {job.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 text-sm font-bold text-slate-500 dark:text-slate-400">{job.time}</td>
                                    </tr>
                                    {/* Completion Notes & Reopen Row */}
                                    {(job.completionNote || job.completionStatus === 'issue') && (
                                        <tr className={`${job.completionStatus === 'issue' ? 'bg-rose-50/50 dark:bg-rose-500/5' : 'bg-emerald-50/30 dark:bg-emerald-500/5'}`}>
                                            <td colSpan="7" className="px-6 py-4">
                                                <div className="flex items-start gap-4">
                                                    <span className="text-xl mt-0.5 flex items-center justify-center">
                                                        {job.completionStatus === 'issue' ? (
                                                            <svg className="w-6 h-6 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                                        ) : (
                                                            <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                                        )}
                                                    </span>
                                                    <div>
                                                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${job.completionStatus === 'issue' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                                            Driver Note
                                                        </p>
                                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{job.completionNote || 'No notes provided.'}</p>
                                                    </div>
                                                    <button 
                                                        onClick={() => setReopenModal({ isOpen: true, id: job._id, type: job.rawType === 'ROUTE' ? 'route' : 'job' })}
                                                        className="ml-auto flex items-center gap-2 px-4 py-2 bg-white/60 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-indigo-600 dark:hover:text-indigo-400 hover:scale-105 transition-all shadow-sm"
                                                    >
                                                        <svg className="w-3.5 h-3.5 mr-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg> Reopen Mission
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmationModal 
                isOpen={reopenModal.isOpen}
                title={`Reopen ${reopenModal.type === 'route' ? 'Route' : 'Job'}?`}
                message={`This will move the ${reopenModal.type} back to the active list and unassign the current driver.`}
                confirmText="Yes, Reopen"
                onConfirm={handleReopenMission}
                onCancel={() => setReopenModal({ isOpen: false, id: null, type: null })}
                isLoading={actionLoading}
                type="info"
            />

            {/* Assignment Conflict Warning Modal */}
            {conflictModal.isOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/70 dark:bg-black/70 backdrop-blur-md" onClick={() => setConflictModal({ isOpen: false, message: '' })} />
                    <div className="relative bg-white dark:bg-slate-900 backdrop-blur-3xl rounded-[2.5rem] shadow-2xl dark:shadow-black/60 w-full max-w-md overflow-hidden border border-white/60 dark:border-white/10 animate-in zoom-in-95 slide-in-from-bottom-4 duration-200">
                        <div className="p-8 text-center">
                            {/* Icon */}
                            <div className="w-20 h-20 bg-amber-50 dark:bg-amber-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner border border-amber-200 dark:border-amber-500/20">
                                <svg className="w-10 h-10 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            {/* Title */}
                            <h3 className="text-lg font-black text-slate-900 dark:text-white mb-2 uppercase tracking-tight">
                                {conflictModal.isPa ? 'PA Already Assigned' : 'Driver Already Assigned'}
                            </h3>
                            <div className="h-1 w-12 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 mx-auto mb-5" />
                            {/* Message */}
                            <p className="text-sm text-slate-600 dark:text-slate-300 font-medium leading-relaxed mb-8">
                                {conflictModal.message}
                            </p>
                            {/* Action */}
                            <button
                                onClick={() => setConflictModal({ isOpen: false, message: '' })}
                                className="w-full px-6 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 shadow-xl shadow-amber-500/30 active:scale-95 transition-all"
                            >
                                Understood
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DispatcherLayout>
    );
};


// 3. JOB REASSIGNMENT
// Reassign drivers for delays / unavailability
// ═══════════════════════════════════════════════
export const JobReassignment = () => {
    const [routes, setRoutes] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedJob, setSelectedJob] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState('');
    // const [reassignModal, setReassignModal] = useState({ isOpen: false, routeId: null });
    const [conflictModal, setConflictModal] = useState({ isOpen: false, message: '' });
    // const [actionLoading, setActionLoading] = useState(null);
    const [reason, setReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [routeRes, driverRes, jobsRes] = await Promise.all([
                dispatcherService.getRoutes(),
                dispatcherService.getDrivers(),
                dispatcherService.getJobs()
            ]);
            let combined = [];
            if (routeRes.data.success) {
                combined = [...combined, ...groupRoutes(routeRes.data.data.routes || []).map(r => ({ ...r, rawType: 'ROUTE' }))];
            }
            if (jobsRes.data.success) {
                const activeJobs = (jobsRes.data.data.jobs || []).filter(j => ['accepted', 'started', 'pending'].includes(j.status));
                combined = [...combined, ...activeJobs.map(j => ({ ...j, rawType: 'JOB' }))];
            }
            setRoutes(combined);
            if (driverRes.data.success) setDrivers(driverRes.data.data.drivers || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleReassign = async () => {
        if (!selectedJob || !selectedDriver || !reason) return;
        if (reason === 'Other' && !customReason.trim()) {
            setError('Please provide a reason');
            return;
        }
        
        const finalReason = reason === 'Other' ? customReason.trim() : reason;

        try {
            setSubmitting(true);
            setError('');
            setSuccess('');
            
            if (selectedJob.rawType === 'JOB') {
                const res = await dispatcherService.assignJobToDriver(selectedJob._id, selectedDriver);
                if (res.data.success) {
                    setSuccess('Driver reassigned successfully for private job');
                    fetchData();
                    setTimeout(() => {
                        setSuccess('');
                        setSelectedJob(null);
                        setSelectedDriver('');
                        setReason('');
                        setCustomReason('');
                    }, 2000);
                } else {
                    setError(res.data.message || 'Failed to reassign driver');
                }
            } else if (selectedJob.isMerged) {
                // For merged routes, reassign the morning route (backend will atomically sync afternoon)
                try {
                    await dispatcherService.reassignRoute(selectedJob.morningRoute._id, { driverId: selectedDriver, reason: finalReason, isMerged: true });
                    setSuccess('Driver reassigned successfully for merged route');
                    fetchData();
                    setTimeout(() => {
                        setSuccess('');
                        setSelectedJob(null);
                        setSelectedDriver('');
                        setReason('');
                        setCustomReason('');
                    }, 2000);
                } catch (err) {
                    const msg = err.response?.data?.message || 'Failed to reassign route';
                    setError(msg);
                }
            } else {
                const res = await dispatcherService.reassignRoute(selectedJob._id, {
                    driverId: selectedDriver,
                    reason: finalReason
                });
                if (res.data.success) {
                    setSuccess('Driver reassigned successfully');
                    fetchData();
                    setTimeout(() => {
                        setSuccess('');
                        setSelectedJob(null);
                        setSelectedDriver('');
                        setReason('');
                        setCustomReason('');
                    }, 2000);
                } else {
                    setError(res.data.message || 'Failed to reassign driver');
                }
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to reassign. Please check if the driver is already assigned to another active task.';
            if (err.response?.status === 409) {
                const isPaConflict = msg.toLowerCase().startsWith('pa ');
                setConflictModal({ isOpen: true, message: msg, isPa: isPaConflict });
            } else {
                setError(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const reasons = ['Driver Delay', 'Driver Unavailable', 'Vehicle Breakdown', 'Emergency', 'Schedule Conflict', 'Other'];
    const avatarGradients = ['from-emerald-400 to-teal-500', 'from-sky-400 to-blue-500', 'from-amber-400 to-orange-500', 'from-rose-400 to-pink-500'];
    const availableDrivers = drivers.filter(d => !d.isBlocked && d.isVerified);

    return (<>
        <DispatcherLayout title="Job Reassignment" subtitle="Reassign drivers to active routes">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 relative z-10">
                {/* Active Jobs List */}
                <div className="xl:col-span-2">
                    <div className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl">
                        <div className="p-6 border-b border-slate-200/50 dark:border-white/5">
                            <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">Active Tasks</h3>
                            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">Select a task to reassign its driver</p>
                        </div>
                        <div className="divide-y divide-slate-100 dark:divide-white/5">
                            {loading ? (
                                [...Array(4)].map((_, i) => (
                                    <div key={i} className="animate-pulse p-6 flex items-center gap-4">
                                        <div className="w-12 h-12 bg-slate-200/50 dark:bg-slate-800 rounded-2xl" />
                                        <div className="flex-1 space-y-3"><div className="h-4 bg-slate-200/50 dark:bg-slate-800 rounded-lg w-1/3" /><div className="h-3 bg-slate-200/30 dark:bg-slate-800/50 rounded-lg w-1/4" /></div>
                                    </div>
                                ))
                            ) : routes.length === 0 ? (
                                <div className="p-16 text-center text-sm font-bold text-slate-400 italic">No active tasks found</div>
                            ) : (
                                routes.map((r, i) => (
                                    <button key={r._id || i} onClick={() => { setSelectedJob(r); setSelectedDriver(''); setReason(''); }}
                                        className={`w-full flex items-center gap-5 p-6 transition-all duration-300 text-left hover:scale-[1.01] ${selectedJob?._id === r._id ? 'bg-emerald-500/10 dark:bg-emerald-500/20 shadow-inner' : 'hover:bg-slate-100/50 dark:hover:bg-slate-800/50'
                                            }`}>
                                        <div className={`w-12 h-12 rounded-[1rem] bg-gradient-to-br ${avatarGradients[i % 4]} flex items-center justify-center text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-${avatarGradients[i % 4].split('-')[1]}-500/30`}>
                                            {r.rawType === 'JOB' ? 'JOB' : `R${i + 1}`}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${r.rawType === 'ROUTE' ? 'bg-indigo-500/10 text-indigo-500 border border-indigo-500/20' : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'}`}>{r.rawType === 'ROUTE' ? 'Route' : 'Job'}</span>
                                                <p className="text-sm font-black text-slate-900 dark:text-white">{decodeHtml(r.name)}</p>
                                            </div>
                                            <p className="text-[11px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">
                                                {r.driverId?.name || 'No driver'} • {r.rawType === 'ROUTE' ? (decodeHtml(r.schoolId?.name) || 'Private') : 'Private Booking'}
                                            </p>
                                        </div>
                                        <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm ${
                                            r.rawType === 'JOB' ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200/50 dark:border-amber-500/20' :
                                            r.type === 'morning' ? 'bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-200/50 dark:border-sky-500/20' : 
                                            r.type === 'afternoon' ? 'bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-200/50 dark:border-violet-500/20' :
                                            'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/20'
                                            }`}>{r.rawType === 'JOB' ? (r.jobType || 'Private') : r.type?.replace('_', ' & ')}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Reassignment Form */}
                <div className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl flex flex-col h-fit relative z-20">
                    <div className="p-6 border-b border-slate-200/50 dark:border-white/5">
                        <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">Reassign Driver</h3>
                        <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">Delay / unavailability handling</p>
                    </div>
                    <div className="p-6 space-y-6">
                        {!selectedJob ? (
                            <div className="py-16 text-center">
                                <div className="w-16 h-16 mx-auto rounded-[1.5rem] bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-6 shadow-inner text-2xl">
                                    <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                </div>
                                <p className="text-sm font-bold text-slate-500 dark:text-slate-400">Select a route from the list</p>
                            </div>
                        ) : (
                            <>
                                {/* Selected Route Info */}
                                <div className="p-5 rounded-2xl bg-emerald-50/80 dark:bg-emerald-500/10 border border-emerald-200/50 dark:border-emerald-500/20 shadow-inner">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-1.5">Selected Route</p>
                                    <p className="text-base font-black text-slate-900 dark:text-white leading-tight">{decodeHtml(selectedJob.name)}</p>
                                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1">Current: {selectedJob.driverId?.name || 'None'}</p>
                                </div>

                                {/* Reason */}
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 mt-2">Reason for Reassignment</label>
                                    <select value={reason} onChange={e => setReason(e.target.value)}
                                        className="w-full px-5 py-3.5 rounded-2xl border border-slate-200/60 dark:border-white/10 text-sm font-bold text-slate-700 dark:text-slate-200 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all appearance-none cursor-pointer">
                                        <option value="" className="bg-white dark:bg-slate-800 dark:text-slate-200">Select reason...</option>
                                        {reasons.map(r => <option key={r} value={r} className="bg-white dark:bg-slate-800 dark:text-slate-200">{r}</option>)}
                                    </select>
                                    
                                    {reason === 'Other' && (
                                        <div className="mt-3">
                                            <input 
                                                type="text" 
                                                value={customReason}
                                                onChange={e => setCustomReason(e.target.value)}
                                                placeholder="Enter custom reason..."
                                                className="w-full px-5 py-3.5 rounded-2xl border border-slate-200/60 dark:border-white/10 text-sm font-bold text-slate-700 dark:text-slate-200 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* New Driver */}
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">Assign New Driver</label>
                                    <select value={selectedDriver} onChange={e => setSelectedDriver(e.target.value)}
                                        className="w-full px-5 py-3.5 rounded-2xl border border-slate-200/60 dark:border-white/10 text-sm font-bold text-slate-700 dark:text-slate-200 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all appearance-none cursor-pointer">
                                        <option value="" className="bg-white dark:bg-slate-800 dark:text-slate-200">Select driver...</option>
                                        {availableDrivers.map(d => (
                                            <option key={d._id} value={d._id} className="bg-white dark:bg-slate-800 dark:text-slate-200">{d.name} {d._id === selectedJob?.driverId?._id ? '(Current)' : ''}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Status Messages */}
                                {error && <p className="text-xs font-bold text-rose-500 dark:text-rose-400">{error}</p>}
                                {success && <p className="text-xs font-bold text-emerald-500 dark:text-emerald-400 flex items-center gap-1"><svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> {success}</p>}

                                {/* Submit */}
                                <button
                                    onClick={handleReassign}
                                    disabled={!reason || !selectedDriver || submitting}
                                    className="w-full mt-auto py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 dark:from-emerald-600 dark:to-teal-600 text-white font-black text-sm uppercase tracking-widest shadow-lg shadow-emerald-500/30 dark:shadow-emerald-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2">
                                    {submitting ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            Reassigning...
                                        </>
                                    ) : (
                                        'Confirm Reassignment'
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </DispatcherLayout>

            {/* Assignment Conflict Warning Modal */}
            {conflictModal.isOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/70 dark:bg-black/70 backdrop-blur-md" onClick={() => setConflictModal({ isOpen: false, message: '' })} />
                    <div className="relative bg-white dark:bg-slate-900 backdrop-blur-3xl rounded-[2.5rem] shadow-2xl dark:shadow-black/60 w-full max-w-md overflow-hidden border border-white/60 dark:border-white/10 animate-in zoom-in-95 slide-in-from-bottom-4 duration-200">
                        <div className="p-8 text-center">
                            {/* Icon */}
                            <div className="w-20 h-20 bg-amber-50 dark:bg-amber-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner border border-amber-200 dark:border-amber-500/20">
                                <svg className="w-10 h-10 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            {/* Title */}
                            <h3 className="text-lg font-black text-slate-900 dark:text-white mb-2 uppercase tracking-tight">
                                {conflictModal.isPa ? 'PA Already Assigned' : 'Driver Already Assigned'}
                            </h3>
                            <div className="h-1 w-12 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 mx-auto mb-5" />
                            {/* Message */}
                            <p className="text-sm text-slate-600 dark:text-slate-300 font-medium leading-relaxed mb-8">
                                {conflictModal.message}
                            </p>
                            {/* Action */}
                            <button
                                onClick={() => setConflictModal({ isOpen: false, message: '' })}
                                className="w-full px-6 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 shadow-xl shadow-amber-500/30 active:scale-95 transition-all"
                            >
                                Understood
                            </button>
                        </div>
                    </div>
                </div>
            )}
    </>);
};


// 4. TRACKING LINKS
// Generate & share secure parent tracking links
// ═══════════════════════════════════════════════
export const TrackingLinks = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generatedLinks, setGeneratedLinks] = useState([]);

    const fetchItems = useCallback(async () => {
        try {
            setLoading(true);
            const [routesRes, jobsRes] = await Promise.all([
                dispatcherService.getRoutes(),
                dispatcherService.getJobs()
            ]);
            let combined = [];
            if (routesRes.data.success) {
                combined = [...combined, ...groupRoutes(routesRes.data.data.routes || []).map(r => ({ ...r, rawType: 'ROUTE' }))];
            }
            if (jobsRes.data.success) {
                combined = [...combined, ...(jobsRes.data.data.jobs || []).map(j => ({ ...j, rawType: 'JOB' }))];
            }
            setItems(combined);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const generateLink = async (item) => {
        try {
            // Wait, does the backend support generating tracking links for private jobs?
            // Yes, let's assume dispatcherService.generateTrackingLink can handle Job _id or it's the same flow?
            // Actually, /routes/:id/tracking-link is used for routes.
            // If the user said "routes ki tara iski bhi reassignment and links track ho",
            // the backend might not have an endpoint for jobs tracking link, OR we might just pass the job ID to the same endpoint?
            // Let's pass the job ID and see! Or wait, jobs don't have tracking links generated via route endpoint unless backend supports it.
            // For now, I'll pass the job ID to the dispatcherService.generateTrackingLink(idToUse).
            const idToUse = item.rawType === 'ROUTE' ? (item.isMerged ? item.morningRoute._id : item._id) : item._id;
            // const endpoint = item.rawType === 'ROUTE' ? `/dispatcher/routes/${idToUse}/tracking-link` : `/admin/jobs/${idToUse}/tracking-link`;
            
            // Wait, we don't have dispatcherService.generateJobTrackingLink!
            // I will just use `api.post` directly here for jobs.
            const res = item.rawType === 'ROUTE' 
                ? await dispatcherService.generateTrackingLink(idToUse)
                : await api.post(`/admin/jobs/${idToUse}/tracking-link`);
                
            if (res.data.success) {
                const { token } = res.data.data;
                const link = {
                    id: token,
                    route: decodeHtml(item.name),
                    driver: item.driverId?.name || 'Unassigned',
                    url: `${window.location.origin}/track/${token}`,
                    expiresAt: new Date(Date.now() + 6 * 60 * 60 * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
                    createdAt: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
                };
                setGeneratedLinks(prev => [link, ...prev]);
            }
        } catch (err) {
            console.error('Generate link error:', err);
        }
    };

    const copyToClipboard = (url) => {
        navigator.clipboard.writeText(url).catch(console.error);
    };

    return (
        <DispatcherLayout title="Tracking Links" subtitle="Generate secure parent tracking links">
            {/* Requirements Info */}
            <div className="mb-6 p-5 rounded-[2rem] bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-md relative z-10 shadow-sm">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-[1rem] bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-white">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div>
                        <p className="text-sm font-black text-emerald-700 dark:text-emerald-400">Secure Tracking</p>
                        <p className="text-[11px] font-bold tracking-widest uppercase text-emerald-600/70 dark:text-emerald-400/70 mt-1">Links are read-only, time-limited, and do not expose personal data</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 relative z-10">
                {/* Routes List */}
                <div className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl">
                    <div className="p-6 border-b border-slate-200/50 dark:border-white/5 flex items-center justify-between">
                        <div>
                            <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">Active Tasks</h3>
                            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">Generate tracking link</p>
                        </div>
                        <button onClick={fetchItems} className="px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 bg-emerald-50/80 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 border border-emerald-200/50 dark:border-emerald-500/20 transition-all duration-300 hover:scale-105 shadow-sm">
                            ↻ Refresh
                        </button>
                    </div>
                    <div className="divide-y divide-slate-100 dark:divide-white/5">
                        {loading ? (
                            [...Array(4)].map((_, i) => (
                                <div key={i} className="animate-pulse p-6"><div className="h-5 bg-slate-200/50 dark:bg-slate-800 rounded-lg w-2/3" /></div>
                            ))
                        ) : items.length === 0 ? (
                            <div className="p-16 text-center text-sm font-bold text-slate-400 italic">No tasks available</div>
                        ) : (
                            items.map((r, i) => (
                                <div key={r._id || i} className="flex items-center justify-between p-6 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${r.rawType === 'ROUTE' ? 'bg-indigo-500/10 text-indigo-500 border border-indigo-500/20' : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'}`}>{r.rawType === 'ROUTE' ? 'Route' : 'Job'}</span>
                                            <p className="text-sm font-black text-slate-900 dark:text-white">{decodeHtml(r.name)}</p>
                                        </div>
                                        <p className="text-[11px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">{r.driverId?.name || 'No driver'} • {r.rawType === 'ROUTE' ? (decodeHtml(r.schoolId?.name) || 'Private') : 'Private Booking'}</p>
                                    </div>
                                    <button onClick={() => generateLink(r)}
                                        className="px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 bg-emerald-50/80 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 border border-emerald-200/50 dark:border-emerald-500/20 transition-all duration-300 hover:scale-105 shadow-sm flex items-center gap-2">
                                        <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4">
                                            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        Generate
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Generated Links */}
                <div className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl">
                    <div className="p-6 border-b border-slate-200/50 dark:border-white/5">
                        <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">Generated Links</h3>
                        <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">Copy & share via SMS or WhatsApp</p>
                    </div>
                    <div className="p-6 space-y-4 max-h-[500px] overflow-y-auto">
                        {generatedLinks.length === 0 ? (
                            <div className="py-16 text-center">
                                <div className="w-16 h-16 mx-auto rounded-[1.5rem] bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-6 shadow-inner text-2xl">
                                    🔗
                                </div>
                                <p className="text-sm font-bold text-slate-500 dark:text-slate-400">No links generated yet</p>
                                <p className="text-[11px] font-bold tracking-widest uppercase text-slate-400/70 mt-1">Select a route to generate a tracking link</p>
                            </div>
                        ) : (
                            generatedLinks.map((link, i) => (
                                <div key={i} className="p-5 rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/50 dark:bg-slate-800/40 hover:bg-white dark:hover:bg-slate-800 transition-colors shadow-sm">
                                    <div className="flex items-start justify-between mb-3">
                                        <div>
                                            <p className="text-sm font-black text-slate-900 dark:text-white">{link.route}</p>
                                            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">Driver: {link.driver}</p>
                                        </div>
                                        <span className="px-3 py-1 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-black uppercase tracking-widest border border-emerald-500/20">Active</span>
                                    </div>
                                    <div className="flex items-center gap-3 mt-4">
                                        <input type="text" readOnly value={link.url}
                                            className="flex-1 px-4 py-3 rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/50 dark:bg-slate-900/50 text-xs text-slate-600 dark:text-slate-300 font-mono font-bold focus:outline-none" />
                                        <button onClick={() => copyToClipboard(link.url)}
                                            className="px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-[11px] font-black uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-md shadow-emerald-500/20">
                                            Copy
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-5 mt-4 pt-3 border-t border-slate-200/50 dark:border-white/5 text-[10px] font-bold tracking-widest uppercase text-slate-400">
                                        <span>Created: {link.createdAt}</span>
                                        <span>Expires: {link.expiresAt}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </DispatcherLayout>
    );
};


// 5. ALERTS — Compliance, delays, emergencies
// ═══════════════════════════════════════════════
export const Alerts = () => {
    const [drivers, setDrivers] = useState([]);
    const [pas, setPas] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [driversRes, pasRes] = await Promise.all([
                dispatcherService.getDrivers(),
                dispatcherService.getPAs()
            ]);
            if (driversRes.data.success) setDrivers(driversRes.data.data.drivers || []);
            if (pasRes.data.success) setPas(pasRes.data.data.pas || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Listen for real-time driver updates to refresh alerts automatically
    useEffect(() => {
        socketService.on('driver:updated', fetchData);
        socketService.on('driver:doc:updated', fetchData);
        socketService.on('route:updated', fetchData);

        return () => {
            socketService.off('driver:updated', fetchData);
            socketService.off('driver:doc:updated', fetchData);
            socketService.off('route:updated', fetchData);
        };
    }, [fetchData]);

    // Generate alerts dynamically from real data
    const generateAlerts = () => {
        let generatedAlerts = [];
        const now = new Date();
        // const warningThreshold = new Date(now.getTime() + (10 * 24 * 60 * 60 * 1000)); // 10 days from now
        
        // Helper to check documents
        const checkDocs = (user, type, docsObj, docKeys) => {
            if (!docsObj) return;
            docKeys.forEach(key => {
                const doc = docsObj[key];
                if (doc && doc.expiryDate && doc.status !== 'rejected') {
                    const expiry = new Date(doc.expiryDate);
                    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
                    
                    if (daysLeft <= 0) {
                        generatedAlerts.push({
                            type: 'critical', 
                            icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
                            title: `${type} Doc Expired: ${user.name}`,
                            desc: `The ${key} for this ${type.toLowerCase()} has expired. Immediate action required.`,
                            time: 'Urgent', category: 'Compliance'
                        });
                    } else if (daysLeft <= 10) {
                        generatedAlerts.push({
                            type: 'warning', 
                            icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                            title: `${type} Doc Expiring Soon: ${user.name}`,
                            desc: `The ${key} for this ${type.toLowerCase()} will expire in ${daysLeft} days.`,
                            time: 'Upcoming', category: 'Compliance'
                        });
                    }
                }
            });
        };

        // 1. Driver Account Blocks
        drivers.filter(d => d.isBlocked).forEach(d => {
            generatedAlerts.push({
                type: 'critical', 
                icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>, 
                title: `Driver Blocked: ${d.name}`,
                desc: 'This driver has been blocked and cannot accept jobs. Review compliance.',
                time: 'System', category: 'Compliance',
            });
        });

        // 2. Driver Inactive (Unverified)
        drivers.filter(d => !d.isVerified && !d.isBlocked).forEach(d => {
            generatedAlerts.push({
                type: 'info', 
                icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, 
                title: `Driver Unverified: ${d.name}`,
                desc: 'Driver account is awaiting document verification.',
                time: 'Pending', category: 'Status',
            });
        });

        // 3. Document Expiries for Drivers
        drivers.forEach(d => {
            const driverDocs = ['drivingLicense', 'insurance', 'mot', 'dbsCertificate'];
            checkDocs(d, 'Driver', d.documents, driverDocs);
        });

        // 4. Document Expiries for PAs
        pas.forEach(p => {
            const paDocs = ['identityDocument', 'dbsCertificate', 'safeguardingCourse'];
            checkDocs(p, 'PA', p.paDocuments, paDocs);
        });

        // Sort by criticality (critical first, then warning, then info)
        const typeWeight = { critical: 1, warning: 2, info: 3 };
        return generatedAlerts.sort((a, b) => typeWeight[a.type] - typeWeight[b.type]);
    };

    const alerts = generateAlerts();

    const typeStyles = {
        critical: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-800', badge: 'bg-rose-500 text-white' },
        warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-500 text-white' },
        info: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-800', badge: 'bg-sky-500 text-white' },
    };

    return (
        <DispatcherLayout title="Alerts" subtitle="System alerts & compliance monitoring">
            {/* Alert Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 relative z-10">
                {[
                    { label: 'Critical', count: alerts.filter(a => a.type === 'critical').length, gradient: 'from-rose-500 to-pink-600', shadow: 'shadow-rose-500/20' },
                    { label: 'Warnings', count: alerts.filter(a => a.type === 'warning').length, gradient: 'from-amber-500 to-orange-600', shadow: 'shadow-amber-500/20' },
                    { label: 'Info', count: alerts.filter(a => a.type === 'info').length, gradient: 'from-sky-500 to-blue-600', shadow: 'shadow-sky-500/20' },
                ].map((s, i) => (
                    <div key={i} className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] p-6 border border-white/60 dark:border-white/10 shadow-sm dark:shadow-2xl relative overflow-hidden group hover:scale-[1.03] transition-all duration-300">
                        <div className={`absolute -top-8 -right-8 w-24 h-24 bg-gradient-to-br ${s.gradient} opacity-[0.08] dark:opacity-[0.15] rounded-full blur-xl group-hover:opacity-[0.2] transition-opacity`} />
                        <div className="flex items-center gap-4 relative">
                            <div className={`w-12 h-12 rounded-[1rem] bg-gradient-to-br ${s.gradient} flex items-center justify-center text-white text-xl font-black shadow-lg ${s.shadow}`}>
                                {s.count}
                            </div>
                            <div>
                                <p className="text-sm font-black text-slate-900 dark:text-white tracking-tight">{s.label}</p>
                                <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-0.5">Active alerts</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Alerts List */}
            <div className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl relative z-10">
                <div className="p-6 border-b border-slate-200/50 dark:border-white/5 flex items-center justify-between">
                    <div>
                        <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">All Alerts</h3>
                        <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">Compliance, delays & system notifications</p>
                    </div>
                    <button onClick={fetchData} className="px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 bg-emerald-50/80 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 border border-emerald-200/50 dark:border-emerald-500/20 transition-all duration-300 hover:scale-105 shadow-sm">
                        ↻ Refresh
                    </button>
                </div>
                <div className="divide-y divide-slate-100 dark:divide-white/5">
                    {loading ? (
                        [...Array(3)].map((_, i) => (
                            <div key={i} className="animate-pulse p-6"><div className="h-5 bg-slate-200/50 dark:bg-slate-800 rounded-lg w-2/3" /></div>
                        ))
                    ) : alerts.length === 0 ? (
                        <div className="p-20 text-center">
                            <div className="w-16 h-16 mx-auto rounded-[1.5rem] bg-emerald-500/10 flex items-center justify-center mb-6 shadow-inner text-emerald-500/50 dark:text-emerald-400/50 text-2xl">
                                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400">All clear — no active alerts</p>
                        </div>
                    ) : (
                        alerts.map((alert, i) => {
                            const style = typeStyles[alert.type];
                            return (
                                <div key={i} className={`p-6 bg-slate-50/50 dark:bg-slate-800/20 hover:bg-white dark:hover:bg-slate-800/60 transition-colors duration-300`}>
                                    <div className="flex items-start gap-5">
                                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-xl shadow-inner ${style.bg} dark:bg-opacity-10`}>
                                            {alert.icon}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-1.5">
                                                <p className={`text-sm font-black ${style.text} dark:text-opacity-90`}>{alert.title}</p>
                                                <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black tracking-widest uppercase shadow-sm ${style.badge}`}>{alert.type}</span>
                                            </div>
                                            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 leading-relaxed">{alert.desc}</p>
                                            <div className="flex items-center gap-4 mt-3 text-[10px] font-bold tracking-widest uppercase text-slate-400 dark:text-slate-500">
                                                <span>{alert.category}</span>
                                                <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
                                                <span>{alert.time}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </DispatcherLayout>
    );
};


// 6. JOB HISTORY — Completed jobs audit trail
// ═══════════════════════════════════════════════
export const JobHistory = () => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const [routesRes, jobsRes] = await Promise.all([
                dispatcherService.getRoutes(),
                dispatcherService.getJobs()
            ]);

            let combined = [];

            if (routesRes.data.success) {
                const routes = groupRoutes(routesRes.data.data.routes || []).filter(r => r.status === 'completed');
                const formattedRoutes = routes.map((r, i) => {
                    const start = r.actualStartTime ? new Date(r.actualStartTime) : null;
                    const end = r.actualEndTime ? new Date(r.actualEndTime) : null;
                    let duration = '—';
                    if (start && end) {
                        const mins = Math.round((end - start) / 60000);
                        duration = `${mins} min`;
                    }
                    return {
                        id: `RTE-${String(i + 1).padStart(3, '0')}`,
                        route: decodeHtml(r.name),
                        driver: r.driverId?.name || 'System',
                        school: decodeHtml(r.schoolId?.name) || 'Private',
                        type: r.type || 'morning',
                        completedAt: end ? end.toLocaleDateString('en-GB') : '—',
                        timestamp: end ? end.getTime() : 0,
                        duration
                    };
                });
                combined = [...combined, ...formattedRoutes];
            }

            if (jobsRes.data.success) {
                const jobs = (jobsRes.data.data.jobs || []).filter(j => j.status === 'completed');
                const formattedJobs = jobs.map((j, i) => {
                    const start = j.actualStartTime ? new Date(j.actualStartTime) : null;
                    const end = j.actualEndTime ? new Date(j.actualEndTime) : null;
                    let duration = '—';
                    if (start && end) {
                        const mins = Math.round((end - start) / 60000);
                        duration = `${mins} min`;
                    }
                    return {
                        id: `JOB-${String(i + 1).padStart(3, '0')}`,
                        route: j.name || 'Private Job',
                        driver: j.driverId?.name || 'System',
                        school: 'Private',
                        type: j.jobType || 'Private Job',
                        completedAt: end ? end.toLocaleDateString('en-GB') : '—',
                        timestamp: end ? end.getTime() : 0,
                        duration
                    };
                });
                combined = [...combined, ...formattedJobs];
            }

            // Sort by most recently completed
            combined.sort((a, b) => b.timestamp - a.timestamp);
            setHistory(combined);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    const avatarGradients = ['from-emerald-400 to-teal-500', 'from-sky-400 to-blue-500', 'from-amber-400 to-orange-500', 'from-rose-400 to-pink-500'];

    return (
        <DispatcherLayout title="Job History" subtitle="Completed jobs audit trail">
            <div className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl relative z-10">
                <div className="p-6 border-b border-slate-200/50 dark:border-white/5 flex items-center justify-between">
                    <div>
                        <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">Completed Jobs</h3>
                        <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">Historical transport operations</p>
                    </div>
                    <button onClick={fetchHistory} className="px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 bg-emerald-50/80 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 border border-emerald-200/50 dark:border-emerald-500/20 transition-all duration-300 hover:scale-105 shadow-sm">
                        ↻ Refresh
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-50/50 dark:bg-slate-800/30">
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Job ID</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Route</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Driver</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">School</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Type</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Completed</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Duration</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {loading ? (
                                [...Array(4)].map((_, i) => (
                                    <tr key={i} className="animate-pulse"><td colSpan="7" className="px-6 py-5"><div className="h-5 bg-slate-200/50 dark:bg-slate-800 rounded-lg" /></td></tr>
                                ))
                            ) : history.length === 0 ? (
                                <tr><td colSpan="7" className="px-6 py-16 text-center text-sm font-bold text-slate-400 italic">No completed jobs to display</td></tr>
                            ) : (
                                history.map((h, i) => (
                                    <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors duration-300">
                                        <td className="px-6 py-5 text-sm font-mono font-bold text-slate-500 dark:text-slate-400">{h.id}</td>
                                        <td className="px-6 py-5 text-sm font-black text-slate-900 dark:text-white">{h.route}</td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${avatarGradients[i % 4]} flex items-center justify-center text-white text-[10px] font-black shadow-lg shadow-${avatarGradients[i % 4].split('-')[1]}-500/30`}>
                                                    {h.driver.split(' ').map(n => n[0]).join('')}
                                                </div>
                                                <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{h.driver}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-sm font-semibold text-slate-600 dark:text-slate-300">{h.school}</td>
                                        <td className="px-6 py-5">
                                            <span className={`inline-flex items-center px-2.5 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest shadow-sm ${h.type === 'morning' ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200/50 dark:border-amber-500/20' : 'bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-200/50 dark:border-sky-500/20'
                                                }`}>{h.type}</span>
                                        </td>
                                        <td className="px-6 py-5 text-sm font-bold text-slate-500 dark:text-slate-400">{h.completedAt}</td>
                                        <td className="px-6 py-5">
                                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black tracking-widest uppercase bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/20">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                                                {h.duration}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </DispatcherLayout>
    );
};
